import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { useDispatch } from "react-redux";
import { AIChatbot } from "../../../features/ai_chatbots/aiChatbotsService";
import aiChatbotsService, { AIChatbotCopyRequest } from "../../../features/ai_chatbots/aiChatbotsService";
import companiesService, { Company } from "../../../features/companies/companiesService";
import { isAdmin, isAgencyOwner, isCompanyOwner } from "../../../utils/permissions";
import { fetchChatbots } from "../../../features/ai_chatbots/aiChatbotsSlice";
import Swal from "sweetalert2";

interface CopyFromChatbotModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const CopyFromChatbotModal: React.FC<CopyFromChatbotModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);

  const [sourceCompanies, setSourceCompanies] = useState<Company[]>([]);
  const [targetCompanies, setTargetCompanies] = useState<Company[]>([]);
  const [sourceCompanyId, setSourceCompanyId] = useState<number | "">("");
  const [targetCompanyId, setTargetCompanyId] = useState<number | "">("");
  const [chatbots, setChatbots] = useState<AIChatbot[]>([]);
  const [chatbotId, setChatbotId] = useState<number | "">("");
  const [title, setTitle] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingChatbots, setLoadingChatbots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isAdminOrSuperadmin = isAdmin(user);
  const isCompanyOwnerRole = isCompanyOwner(user) && !isAdmin(user);
  const isAgencyOwnerRole = isAgencyOwner(user) && !isAdmin(user);

  // Company owner: tek şirket sabit
  const [companyOwnerCompanyId, setCompanyOwnerCompanyId] = useState<number | null>(null);

  useEffect(() => {
    if (!show || !user) return;
    const load = async () => {
      setLoadingCompanies(true);
      try {
        if (isAdminOrSuperadmin) {
          const { companies: list } = await companiesService.getCompanies({ limit: 1000 });
          setSourceCompanies(list || []);
          setTargetCompanies(list || []);
        } else if (isAgencyOwnerRole) {
          const list = await companiesService.getMyAgencyCompanies();
          const arr = Array.isArray(list) ? list : [];
          setSourceCompanies(arr);
          setTargetCompanies(arr);
        } else if (isCompanyOwnerRole) {
          const c = await companiesService.getMyCompany();
          if (c) {
            setCompanyOwnerCompanyId(c.id);
            setSourceCompanies([c]);
            setTargetCompanies([c]);
          } else {
            setCompanyOwnerCompanyId(null);
            setSourceCompanies([]);
            setTargetCompanies([]);
          }
        } else {
          setSourceCompanies([]);
          setTargetCompanies([]);
        }
      } catch {
        setSourceCompanies([]);
        setTargetCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };
    load();
  }, [show, user, isAdminOrSuperadmin, isAgencyOwnerRole, isCompanyOwnerRole]);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setSourceCompanyId("");
      setTargetCompanyId("");
      setChatbots([]);
      setChatbotId("");
      setTitle("");
    }
  }, [show]);

  // Company owner: when company is loaded, source/target sabit (kendi şirketi)
  useEffect(() => {
    if (show && isCompanyOwnerRole && companyOwnerCompanyId != null) {
      setSourceCompanyId(companyOwnerCompanyId);
      setTargetCompanyId(companyOwnerCompanyId);
    }
  }, [show, isCompanyOwnerRole, companyOwnerCompanyId]);

  // Load chatbots when source company is selected
  const effectiveSourceCompanyId = isCompanyOwnerRole ? companyOwnerCompanyId : sourceCompanyId;
  useEffect(() => {
    if (!show || effectiveSourceCompanyId === "" || effectiveSourceCompanyId == null) {
      setChatbots([]);
      setChatbotId("");
      return;
    }
    const load = async () => {
      setLoadingChatbots(true);
      try {
        const res = await aiChatbotsService.getChatbots({
          company_id: Number(effectiveSourceCompanyId),
          limit: 500,
          skip: 0,
        });
        const list = res?.data ?? res?.chatbots ?? (Array.isArray(res) ? res : []);
        setChatbots(list);
        setChatbotId("");
      } catch {
        setChatbots([]);
        setChatbotId("");
      } finally {
        setLoadingChatbots(false);
      }
    };
    load();
  }, [show, effectiveSourceCompanyId]);

  const effectiveTargetCompanyId = isCompanyOwnerRole ? companyOwnerCompanyId : targetCompanyId;
  const canSubmit =
    chatbotId !== "" &&
    (effectiveTargetCompanyId !== "" && effectiveTargetCompanyId != null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (chatbotId === "" || effectiveTargetCompanyId == null || effectiveTargetCompanyId === "") return;
    setSubmitting(true);
    try {
      const body: AIChatbotCopyRequest = {
        target_company_id: Number(effectiveTargetCompanyId),
        name: title.trim() || undefined,
      };
      await aiChatbotsService.copyChatbot(Number(chatbotId), body);
      Swal.fire({
        title: t("success"),
        text: t("chatbot_copied_successfully"),
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      onSuccess();
      onHide();
      dispatch(fetchChatbots({}));
    } catch (err: any) {
      Swal.fire({
        title: t("error"),
        text: err?.response?.data?.detail || err?.message || t("failed_to_copy_chatbot"),
        icon: "error",
        confirmButtonText: t("ok"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("copy_from")}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Source company: admin, agency_owner seçer; company_owner için sabit (gösterilmez veya readonly) */}
          {!isCompanyOwnerRole && (
            <Form.Group className="mb-3">
              <Form.Label>{t("source_company")}</Form.Label>
              <Form.Select
                value={sourceCompanyId}
                onChange={(e) => setSourceCompanyId(e.target.value ? Number(e.target.value) : "")}
                required={!isCompanyOwnerRole}
                disabled={loadingCompanies}
              >
                <option value="">{loadingCompanies ? t("loading_companies") : t("select_company")}</option>
                {sourceCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name} {c.company_type ? `(${c.company_type})` : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          {isCompanyOwnerRole && companyOwnerCompanyId != null && (
            <Form.Group className="mb-3">
              <Form.Label>{t("source_company")}</Form.Label>
              <Form.Control
                type="text"
                value={sourceCompanies.find((c) => c.id === companyOwnerCompanyId)?.company_name ?? ""}
                readOnly
                disabled
              />
            </Form.Group>
          )}

          {/* Chatbot select */}
          <Form.Group className="mb-3">
            <Form.Label>{t("chatbot")}</Form.Label>
            <Form.Select
              value={chatbotId}
              onChange={(e) => setChatbotId(e.target.value ? Number(e.target.value) : "")}
              required
              disabled={loadingChatbots || (effectiveSourceCompanyId === "" || effectiveSourceCompanyId == null)}
            >
              <option value="">
                {loadingChatbots
                  ? t("loading")
                  : effectiveSourceCompanyId === "" || effectiveSourceCompanyId == null
                  ? t("select_company_first")
                  : t("select_chatbot")}
              </option>
              {chatbots.map((cb) => (
                <option key={cb.id} value={cb.id}>
                  {cb.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Target company: admin ve agency_owner seçer; company_owner için yok (kendi şirketi) */}
          {!isCompanyOwnerRole && (
            <Form.Group className="mb-3">
              <Form.Label>{t("target_company")}</Form.Label>
              <Form.Select
                value={targetCompanyId}
                onChange={(e) => setTargetCompanyId(e.target.value ? Number(e.target.value) : "")}
                required
                disabled={loadingCompanies}
              >
                <option value="">{loadingCompanies ? t("loading_companies") : t("select_company")}</option>
                {targetCompanies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name} {c.company_type ? `(${c.company_type})` : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* Title */}
          <Form.Group className="mb-3">
            <Form.Label>{t("new_chatbot_name_optional")}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t("new_chatbot_name_placeholder")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="modern-modal-footer border-0 pt-0 pb-4 justify-content-end gap-2">
          <Button
            variant="light"
            className="new-modal-btn-cancel"
            onClick={onHide}
            disabled={submitting}
          >
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            className="new-modal-btn-confirm"
            disabled={submitting || !canSubmit}
          >
            {submitting ? t("loading") + "..." : t("copy")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CopyFromChatbotModal;
